import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { useSelector } from 'react-redux'

import { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Note } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InlineErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineErrorBanner/InlineErrorBanner'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  getAuthIsLTSupervisor,
  getAuthRoles,
  getAuthSupervisorId,
  getAuthSupervisorRoles,
  getAuthUserId,
} from '../../../data/auth/authSelectors'
import { updateEndpointCache } from '../../../data/rtkQuery'
import { useAppDispatch } from '../../../data/store'
import { setToastContent } from '../../data/ltToastAutoActions'
import notesApi, { useDeleteNoteMutation } from '../data/notesApi'
import { CARE_REFERRAL_DELETE_MESSAGES as MESSAGES } from '../utils/careReferralDeleteMessages'
import { canDeleteCareReferralDraft } from '../utils/careReferralDeleteUtils'

type UseDeleteCareReferralDraftArgs = {
  /** Called after a successful delete, e.g. to refresh a list that keeps notes in local state. */
  onDeleted?: (noteId: string) => void
}

/**
 * Delete flow for a draft Care Referral (LCDELIVERY-7932): permission check, confirmation modal,
 * the DELETE call, cache cleanup, success toast, and an inline error in the modal on failure.
 * Callers render `confirmationModal` once and wire their own "Delete" link to `requestDelete`,
 * shown only when `canDelete(note)` is true.
 */
export const useDeleteCareReferralDraft = ({ onDeleted }: UseDeleteCareReferralDraftArgs = {}) => {
  const intl = useIntl()
  const { spacing } = useTheme()
  const dispatch = useAppDispatch()
  const userId = useSelector(getAuthUserId)
  const userRoles = useSelector(getAuthRoles)
  const isSupervisor = useSelector(getAuthIsLTSupervisor)
  const supervisorId = useSelector(getAuthSupervisorId)
  const supervisorRoles = useSelector(getAuthSupervisorRoles)
  const { isCareReferralDeleteEnabled } = useFlags()
  const [deleteNote, { isLoading: isDeleting }] = useDeleteNoteMutation()
  // The draft awaiting confirmation, and the name of the client it's for (shown in the modal).
  const [pending, setPending] = useState<{ noteId: string; clientName: string } | null>(null)
  // True after a failed delete; shows the inline error banner while the modal stays open.
  const [deleteFailed, setDeleteFailed] = useState(false)

  // The backend checks whoever is signed in. When a supervisor is signed in, that's the
  // supervisor, not the provider whose clients they're viewing.
  const signedInLyraId = isSupervisor ? supervisorId : userId
  const signedInRoles = isSupervisor ? supervisorRoles : userRoles

  // The feature flag is the kill switch for the whole flow: with it off, no one can open the modal.
  const canDelete = useCallback(
    (note: Note | null | undefined) =>
      !!isCareReferralDeleteEnabled && canDeleteCareReferralDraft({ note, signedInLyraId, signedInRoles }),
    [isCareReferralDeleteEnabled, signedInLyraId, signedInRoles],
  )

  /** Opens the confirmation modal. `clientName` is the client the draft is for. */
  const requestDelete = useCallback(
    (note: Note, clientName: string) => {
      if (canDelete(note)) {
        setDeleteFailed(false)
        setPending({ noteId: note.id, clientName })
      }
    },
    [canDelete],
  )

  const closeModal = useCallback(() => {
    if (!isDeleting) {
      setPending(null)
      setDeleteFailed(false)
    }
  }, [isDeleting])

  const confirmDelete = useCallback(async () => {
    if (!pending) {
      return
    }
    const { noteId } = pending
    setDeleteFailed(false)
    try {
      await deleteNote({ noteId }).unwrap()
    } catch (error) {
      // A 404 means the draft is already gone (deleted in another tab or by a supervisor, or a
      // lost response), which is the outcome the user wanted, so fall through to the success path.
      // Any other failure (403/405 or a network error) keeps the modal open with an inline error
      // so the user can retry or cancel; the note and lists are left as they are.
      if ((error as { response?: { status?: number } } | undefined)?.response?.status !== 404) {
        setDeleteFailed(true)
        return
      }
    }

    // Episode-scoped note lists aren't tag-invalidated by the mutation, so remove the note from
    // every cached getNotes result directly.
    dispatch(updateEndpointCache(notesApi, 'getNotes').build((notes) => notes.filter((n) => n.id !== noteId)))
    setPending(null)
    dispatch(
      setToastContent({
        text: intl.formatMessage(MESSAGES.successToast),
        id: 'DeleteCareReferralDraft-success',
        toastType: 'success',
      }),
    )
    onDeleted?.(noteId)
  }, [pending, deleteNote, dispatch, intl, onDeleted])

  const confirmationModal = (
    <ConfirmationModal
      visible={pending !== null}
      modalTitle={intl.formatMessage(MESSAGES.modalTitle)}
      modalContents={
        <View>
          <BodyText text={intl.formatMessage(MESSAGES.modalBody, { clientName: pending?.clientName ?? '' })} />
          {deleteFailed && (
            <InlineErrorBanner
              text={intl.formatMessage(MESSAGES.errorBanner)}
              marginTop={spacing['24px']}
              testId={tID('DeleteCareReferralDraft-error-banner')}
            />
          )}
        </View>
      }
      confirmationButtonText={intl.formatMessage(MESSAGES.confirmButton)}
      cancelButtonText={intl.formatMessage(MESSAGES.cancelButton)}
      onConfirmationButtonPress={confirmDelete}
      onRequestClose={closeModal}
      onCancelPressed={closeModal}
      confirmButtonLoading={isDeleting}
      cancelButtonDisabled={isDeleting}
      // Mobile-sized screens render a bottom sheet that can be swiped or tapped away; closeModal ignores
      // that while a delete is in flight, which would leave this hook's state out of step with the sheet.
      disableClose={isDeleting}
      testID={tID('DeleteCareReferralDraft-confirmation-modal')}
    />
  )

  return { canDelete, requestDelete, confirmationModal, isDeleting }
}
